import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import {
  Tabs, Tab, Box, IconButton, Chip, LinearProgress, Tooltip, Fade,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';

const DoctorEntryEdit = () => {
  const history = useHistory();
  const { id } = useParams();
  const { isAuthenticated, companyId, branchId, userId, token, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [formProgress, setFormProgress] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  const [banner, setBanner] = useState({ show: false, message: '', type: '' });
  const [sectionCompletion, setSectionCompletion] = useState({
    patientInfo: false,
    observations: false,
    prescriptions: false,
    treatments: false,
    Questionnaire: false
  });

  // Patient Selection
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [treatmentsList, setTreatmentsList] = useState([]);
  const [treatmentSessions, setTreatmentSessions] = useState([]);
  const [QuestionsList, setQuestionsList] = useState([]);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');

  // Main Form Data
  const [formData, setFormData] = useState({
    CompanyId:companyId,
    CreatedBy:userId,
    PatientId: '',
    patientName: '',
    patientMRD: '',
    patientPhone: '',
    DoctorId: '',
    DoctorEntryDate: getCurrentDate(),
    ChiefComplaint: '',
    Diagnosis: ''
  });

  // Observations
  const [observations, setObservations] = useState([]);
  const [currentObservation, setCurrentObservation] = useState({
    ObservationType: '',
    ObservationValue: '',
    Unit: '',
    Remarks: ''
  });

  // Prescriptions
  const [prescriptions, setPrescriptions] = useState([]);
  const [currentPrescription, setCurrentPrescription] = useState({
    ItemId: '',
    MedicineName: '',
    Dosage: '',
    Frequency: '',
    Duration: '',
    Instructions: '',
    Quantity: 1,
  });

  // Treatments
  const [treatments, setTreatments] = useState([]);
  const [currentTreatment, setCurrentTreatment] = useState({
    TreatmentId: '',
    TreatmentName: '',
    SessionId: '',
    TreatmentSessionName: '',
    Description: '',
    StartDate: getCurrentDate(),
    EndDate: getCurrentDate(),
    Rate: '',
    SittingOrSession: 1,
  });

  // Questionnaire
  const [Questionnaire, setQuestionnaire] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({
    QuestioniertId: '',
    Question: '',
    Answer: '',
    AnswerType: 'text' // 'text' or 'select'
  });



  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
    fetchDoctorEntryData();
    fetchPatients();
    fetchDoctors();
    fetchMedicines();
    fetchTreatmentsList();
    fetchQuestionsList();
  }, [isAuthenticated, companyId, token, logout, id]);

  // Fetch existing doctor entry data
  const fetchDoctorEntryData = async () => {
    setLoadingData(true);
    try {
      const response = await fetchData('DoctorEntryApi', `/${id}`, token);
      if (response.responseCode === '1111' && response.response_data) {
        const data = response.response_data;
        
        // Set main form data
        setFormData({
          CompanyId: companyId,
          ModifiedBy: userId,
          Id:data.DoctorEntry.Id,
          AppointmentId:data.DoctorEntry.AppointmentId,
          IsActive:data.DoctorEntry.IsActive,
          CreatedBy:data.DoctorEntry.CreatedBy,
          CreatedDate:data.DoctorEntry.CreatedDate,
          PatientId: data.DoctorEntry.PatientId || '',
          patientName: data.DoctorEntry.PatientName || '',
          patientMRD: data.DoctorEntry.MRDNo || '',
          patientPhone: data.DoctorEntry.ContactNumber1 || '',
          DoctorId: data.DoctorEntry.DoctorId || '',
          DoctorEntryDate: data.DoctorEntry.DoctorEntryDate ? data.DoctorEntry.DoctorEntryDate.split('T')[0] : getCurrentDate(),
          ChiefComplaint: data.DoctorEntry.ChiefComplaint || '',
          Diagnosis: data.DoctorEntry.Diagnosis || ''
          
        });

        // Set observations
        if (data.ObservationDetails && Array.isArray(data.ObservationDetails)) {
          setObservations(data.ObservationDetails);
        }

        // Set prescriptions
        if (data.PrescriptionDetails && Array.isArray(data.PrescriptionDetails)) {
          setPrescriptions(data.PrescriptionDetails);
        }

        // Set treatments
        if (data.TreatmentDetails && Array.isArray(data.TreatmentDetails)) {
          setTreatments(data.TreatmentDetails);
        }

        // Set questionnaire
        if (data.TreatmentQuestionier && Array.isArray(data.TreatmentQuestionier)) {
          setQuestionnaire(data.TreatmentQuestionier);
        }

        showBanner('Doctor entry loaded successfully', 'success');
      } else {
        showBanner('Failed to load doctor entry', 'error');
        setTimeout(() => history.push('/DoctorEntryIndex'), 2000);
      }
    } catch (error) {
      console.error('Error fetching doctor entry:', error);
      showBanner('Failed to load doctor entry', 'error');
      setTimeout(() => history.push('/DoctorEntryIndex'), 2000);
    } finally {
      setLoadingData(false);
    }
  };

  // Calculate form progress and section completion
  useEffect(() => {
    const requiredFields = [
      formData.PatientId,
      formData.DoctorId,
      formData.ChiefComplaint,
      observations.length > 0,
      prescriptions.length > 0
    ];
    const filledFields = requiredFields.filter(field => field).length;
    const progress = (filledFields / requiredFields.length) * 100;
    setFormProgress(Math.round(progress));

    // Update section completion
    setSectionCompletion({
      patientInfo: !!(formData.PatientId && formData.DoctorId && formData.ChiefComplaint),
      observations: observations.length > 0,
      prescriptions: prescriptions.length > 0,
      treatments: treatments.length > 0,
      Questionnaire: Questionnaire.length > 0
    });
  }, [formData, observations, prescriptions, treatments, Questionnaire]);

  // Validate input fields
  const validateField = (field, value) => {
    const errors = { ...validationErrors };

    switch (field) {
      case 'ChiefComplaint':
        if (value && value.length < 10) {
          errors[field] = 'Chief complaint should be at least 10 characters';
        } else {
          delete errors[field];
        }
        break;
      case 'Diagnosis':
        if (value && value.length < 5) {
          errors[field] = 'Diagnosis should be at least 5 characters';
        } else {
          delete errors[field];
        }
        break;
      default:
        break;
    }

    setValidationErrors(errors);
  };

  function getCurrentDate() {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }

  const showBanner = (message, type = 'info') => {
    setBanner({ show: true, message, type });
    setTimeout(() => {
      setBanner({ show: false, message: '', type: '' });
    }, 4000);
  };

  const [loadingPatients, setLoadingPatients] = useState(false);

  const fetchPatients = async () => {
    setLoadingPatients(true);
    try {
      const response = await fetchData('PatientApi', `/${companyId}`, token);
      if (response.responseCode === '1111') {
        setPatients(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoadingPatients(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await fetchData('DoctorApi', `/${companyId}`, token);
      if (response.responseCode === '1111') {
        setDoctors(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const fetchMedicines = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      if (response.responseCode === '1111') {
        setMedicines(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching medicines:', error);
    }
  };

  const fetchTreatmentsList = async () => {
    try {
      const response = await fetchData('TreatmentApi', `/${companyId}`, token);
      if (response.responseCode === '1111') {
        setTreatmentsList(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching treatments:', error);
    }
  };

  const fetchTreatmentSessions = async (TreatmentId) => {
    try {
      const response = await fetchData('TreatmentSessionApi', `/TreatmentSession/${companyId}/${TreatmentId}`, token);
      if (response.responseCode === '1111') {
        setTreatmentSessions(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching treatment sessions:', error);
      setTreatmentSessions([]);
    }
  };

  const fetchQuestionsList = async () => {
    try {
      const response = await fetchData('QuestionierApi', `/${companyId}`, token);
      if (response.responseCode === '1111') {
        setQuestionsList(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching Questions:', error);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  // Observation Handlers
  const addObservation = () => {
    if (!currentObservation.ObservationType || !currentObservation.ObservationValue) {
      showBanner('Please fill observation type and value', 'warning');
      return;
    }
    setObservations([...observations, { ...currentObservation }]);
    setCurrentObservation({ ObservationType: '', ObservationValue: '', Unit: '', Remarks: '' });
    showBanner('Observation added successfully', 'success');
  };

  const deleteObservation = (index) => {
    setObservations(observations.filter((_, i) => i !== index));
  };

  // Prescription Handlers
  const addPrescription = () => {
    if (!currentPrescription.MedicineName || !currentPrescription.Dosage) {
      showBanner('Please fill medicine name and dosage', 'warning');
      return;
    }
    setPrescriptions([...prescriptions, { ...currentPrescription }]);
    setCurrentPrescription({ ItemId: '', MedicineName: '', Dosage: '', Frequency: '', Duration: '', Instructions: '' });
    showBanner('Prescription added successfully', 'success');
  };

  const deletePrescription = (index) => {
    setPrescriptions(prescriptions.filter((_, i) => i !== index));
  };

  // Treatment Handlers
  const addTreatment = () => {
    if (!currentTreatment.TreatmentId || !currentTreatment.TreatmentName) {
      showBanner('Please select treatment', 'warning');
      return;
    }
    setTreatments([...treatments, { ...currentTreatment }]);
    setCurrentTreatment({
      TreatmentId: '',
      TreatmentName: '',
      SessionId: '',
      TreatmentSessionName: '',
      Description: '',
      StartDate: getCurrentDate(),
      EndDate: '',
      Rate: ''
    });
    setTreatmentSessions([]);
    showBanner('Treatment added successfully', 'success');
  };

  const deleteTreatment = (index) => {
    setTreatments(treatments.filter((_, i) => i !== index));
  };

  // Questionnaire Handlers
  const addQuestion = () => {
    if (!currentQuestion.Question || !currentQuestion.Answer) {
      showBanner('Please fill question and answer', 'warning');
      return;
    }
    setQuestionnaire([...Questionnaire, { ...currentQuestion }]);
    setCurrentQuestion({ QuestioniertId: '', Question: '', Answer: '', AnswerType: 'text' });
    showBanner('Question added successfully', 'success');
  };

  const deleteQuestion = (index) => {
    setQuestionnaire(Questionnaire.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.PatientId || !formData.DoctorId) {
      showBanner('Please select patient and doctor', 'error');
      return;
    }

    const payload = {
      DoctorEntry: formData,
      ObservationDetails: observations,
      PrescriptionDetails: prescriptions,
      TreatmentDetails: treatments,
      TreatmentQuestionier: Questionnaire,
    };

    try {
      setLoading(true);
      const response = await updateData('DoctorEntryApi', id, payload, token);
      if (response.responseCode === '1111') {
        showBanner('Doctor Entry updated successfully!', 'success');
        setTimeout(() => {
          history.push('/DoctorEntryIndex');
        }, 1500);
      }
    } catch (error) {
      console.error('Error updating doctor entry:', error);
      showBanner('Failed to update doctor entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '2px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  };

  const inputStyle = {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '13px',
    transition: 'all 0.2s ease',
    outline: 'none',
    height: '32px'
  };

  const buttonStyle = {
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  };

  if (loadingData) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid rgba(255, 255, 255, 0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          color: 'white',
          marginTop: '20px'
        }}>
          Loading Doctor Entry...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ padding: '10px', maxWidth: '100%', position: 'relative' }}>
        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{
              background: 'white',
              padding: '40px 60px',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              animation: 'scaleIn 0.3s ease-out'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid #e5e7eb',
                borderTop: '4px solid #667eea',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                textAlign: 'center'
              }}>
                Updating Doctor Entry...
              </div>
              <div style={{
                fontSize: '13px',
                color: '#6b7280',
                textAlign: 'center'
              }}>
                Please wait while we save your changes
              </div>
            </div>
          </div>
        )}

        {/* Banner Notification */}
        {banner.show && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            minWidth: '300px',
            maxWidth: '500px',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideInRight 0.4s ease-out',
            background: banner.type === 'success' 
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : banner.type === 'error'
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : banner.type === 'warning'
              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white'
          }}>
            <span style={{ fontSize: '24px' }}>
              {banner.type === 'success' ? '✅' : banner.type === 'error' ? '❌' : banner.type === 'warning' ? '⚠️' : 'ℹ️'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>
                {banner.type === 'success' ? 'Success' : banner.type === 'error' ? 'Error' : banner.type === 'warning' ? 'Warning' : 'Info'}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.95 }}>{banner.message}</div>
            </div>
            <button
              onClick={() => setBanner({ show: false, message: '', type: '' })}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                cursor: 'pointer',
                padding: '4px 8px',
                fontSize: '18px',
                lineHeight: '1',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              ×
            </button>
          </div>
        )}
        <style>{`
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(100px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.8);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes shimmer {
            0% { background-position: -1000px 0; }
            100% { background-position: 1000px 0; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes checkmark {
            0% { transform: scale(0) rotate(45deg); }
            50% { transform: scale(1.2) rotate(45deg); }
            100% { transform: scale(1) rotate(45deg); }
          }
          @keyframes fadeInUp {
            from { 
              opacity: 0; 
              transform: translateY(10px); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0); 
            }
          }
          @keyframes tableRowAppear {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .advanced-table {
            animation: fadeInUp 0.5s ease-out;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          }
          .table-row-animated {
            animation: tableRowAppear 0.4s ease-out;
            transition: all 0.3s ease;
          }
          .table-row-animated:hover {
            background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%) !important;
            transform: translateX(4px);
            box-shadow: -4px 0 0 0 #8b9aed;
          }
          .input-field {
            transition: all 0.2s ease;
          }
          .input-field:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          }
          .input-field.error {
            border-color: #ef4444;
            background-color: #fef2f2;
          }
          .input-field.error:focus {
            box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
          }
          .input-field.success {
            border-color: #10b981;
            background-color: #f0fdf4;
          }
          select.input-field {
            border-color: #4ade80;
            background-color: #f0fdf4;
          }
          select.input-field:focus {
            border-color: #22c55e;
            box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.1);
          }
          .tab-content {
            animation: slideIn 0.4s ease-out;
          }
          .shimmer-loading {
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 1000px 100%;
            animation: shimmer 2s infinite;
          }
          .completion-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 5px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            transition: all 0.3s ease;
          }
          .completion-badge.completed {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
          }
          .completion-badge.incomplete {
            background: #f3f4f6;
            color: #9ca3af;
          }
          .checkmark {
            display: inline-block;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: white;
            position: relative;
          }
          .checkmark::after {
            content: '';
            position: absolute;
            left: 4px;
            top: 2px;
            width: 3px;
            height: 6px;
            border: solid #10b981;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
            animation: checkmark 0.3s ease;
          }
        `}</style>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          marginTop: '-30px'
        }}>
          {/* Progress Bar */}
          <LinearProgress
            variant="determinate"
            value={formProgress}
            style={{ height: '3px' }}
            sx={{
              backgroundColor: '#e0f2e9',
              '& .MuiLinearProgress-bar': {
                background: formProgress === 100
                  ? 'linear-gradient(90deg, #059669 0%, #10b981 100%)'
                  : 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
              }
            }}
          />

          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            padding: '8px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px' }}>✏️</span>
              <div>
                <h1 style={{ fontSize: '15px', fontWeight: '700', color: 'white', margin: 0, lineHeight: '1.2' }}>
                  Edit Doctor Entry - Patient Record
                </h1>
                <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)', margin: '2px 0 0 0', lineHeight: '1.3' }}>
                  Update patient examination and treatment documentation
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Tooltip title="Patient & Doctor Information" arrow placement="bottom">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  height: '24px',
                  background: sectionCompletion.patientInfo
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                    : 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(12px)',
                  border: sectionCompletion.patientInfo
                    ? '1px solid rgba(255, 255, 255, 0.3)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: sectionCompletion.patientInfo
                    ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'default'
                }}>
                  <span style={{ fontSize: '12px', lineHeight: '1' }}>👤</span>
                  <span style={{
                    fontSize: '9px',
                    fontWeight: '700',
                    color: 'white',
                    letterSpacing: '0.3px'
                  }}>
                    Patient
                  </span>
                  {sectionCompletion.patientInfo && (
                    <span style={{
                      fontSize: '10px',
                      color: 'white',
                      animation: 'checkmark 0.5s ease'
                    }}>✓</span>
                  )}
                </div>
              </Tooltip>

              <Tooltip title="Click to view Observations" arrow placement="bottom">
                <div
                  onClick={() => setActiveTab(0)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    height: '24px',
                    background: sectionCompletion.observations
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                      : 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(12px)',
                    border: sectionCompletion.observations
                      ? '1px solid rgba(255, 255, 255, 0.3)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: sectionCompletion.observations
                      ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer'
                  }}>
                  <span style={{ fontSize: '12px', lineHeight: '1' }}>🩺</span>
                  <span style={{
                    fontSize: '9px',
                    fontWeight: '700',
                    color: 'white',
                    letterSpacing: '0.3px'
                  }}>
                    Observations
                  </span>
                  {sectionCompletion.observations && (
                    <span style={{
                      fontSize: '10px',
                      color: 'white',
                      animation: 'checkmark 0.5s ease'
                    }}>✓</span>
                  )}
                </div>
              </Tooltip>

              <Tooltip title="Click to view Prescriptions" arrow placement="bottom">
                <div
                  onClick={() => setActiveTab(1)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    height: '24px',
                    background: sectionCompletion.prescriptions
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                      : 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(12px)',
                    border: sectionCompletion.prescriptions
                      ? '1px solid rgba(255, 255, 255, 0.3)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: sectionCompletion.prescriptions
                      ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer'
                  }}>
                  <span style={{ fontSize: '12px', lineHeight: '1' }}>💊</span>
                  <span style={{
                    fontSize: '9px',
                    fontWeight: '700',
                    color: 'white',
                    letterSpacing: '0.3px'
                  }}>
                    Prescriptions
                  </span>
                  {sectionCompletion.prescriptions && (
                    <span style={{
                      fontSize: '10px',
                      color: 'white',
                      animation: 'checkmark 0.5s ease'
                    }}>✓</span>
                  )}
                </div>
              </Tooltip>

              <Tooltip title="Click to view Treatments" arrow placement="bottom">
                <div
                  onClick={() => setActiveTab(2)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    height: '24px',
                    background: sectionCompletion.treatments
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                      : 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(12px)',
                    border: sectionCompletion.treatments
                      ? '1px solid rgba(255, 255, 255, 0.3)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: sectionCompletion.treatments
                      ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer'
                  }}>
                  <span style={{ fontSize: '12px', lineHeight: '1' }}>🏥</span>
                  <span style={{
                    fontSize: '9px',
                    fontWeight: '700',
                    color: 'white',
                    letterSpacing: '0.3px'
                  }}>
                    Treatments
                  </span>
                  {sectionCompletion.treatments && (
                    <span style={{
                      fontSize: '10px',
                      color: 'white',
                      animation: 'checkmark 0.5s ease'
                    }}>✓</span>
                  )}
                </div>
              </Tooltip>

              <Tooltip title="Click to view Questionnaire" arrow placement="bottom">
                <div
                  onClick={() => setActiveTab(3)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    height: '24px',
                    background: sectionCompletion.Questionnaire
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                      : 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(12px)',
                    border: sectionCompletion.Questionnaire
                      ? '1px solid rgba(255, 255, 255, 0.3)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: sectionCompletion.Questionnaire
                      ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer'
                  }}>
                  <span style={{ fontSize: '12px', lineHeight: '1' }}>❓</span>
                  <span style={{
                    fontSize: '9px',
                    fontWeight: '700',
                    color: 'white',
                    letterSpacing: '0.3px'
                  }}>
                    Questions
                  </span>
                  {sectionCompletion.Questionnaire && (
                    <span style={{
                      fontSize: '10px',
                      color: 'white',
                      animation: 'checkmark 0.5s ease'
                    }}>✓</span>
                  )}
                </div>
              </Tooltip>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '3px 10px',
                borderRadius: '12px',
                height: '24px',
                background: formProgress === 100
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)'
                  : 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: formProgress === 100
                  ? '0 4px 12px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <span style={{ fontSize: '14px', lineHeight: '1' }}>📊</span>
                <span style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: 'white',
                  letterSpacing: '0.5px'
                }}>
                  {formProgress}%
                </span>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '6px', marginLeft: '8px', borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '8px' }}>                
                <Tooltip title="Cancel and go back" arrow>
                  <button
                    onClick={() => history.goBack()}
                    style={{
                      ...buttonStyle,
                      padding: '4px 10px',
                      height: '26px',
                      fontSize: '10px',
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <span style={{ fontSize: '12px' }}>❌</span>
                    Cancel
                  </button>
                </Tooltip>
                <Tooltip
                  title={formProgress < 60 ? 'Complete at least 60% of the form to update' : 'Update doctor entry'}
                  arrow
                >
                  <span>
                    <button
                      onClick={handleSubmit}
                      disabled={loading || formProgress < 60}
                      style={{
                        ...buttonStyle,
                        padding: '4px 10px',
                        height: '26px',
                        fontSize: '10px',
                        background: loading || formProgress < 60
                          ? 'rgba(255,255,255,0.3)'
                          : 'rgba(255,255,255,0.95)',
                        color: loading || formProgress < 60 ? 'rgba(255,255,255,0.7)' : '#667eea',
                        border: '1px solid rgba(255,255,255,0.4)',
                        opacity: loading || formProgress < 60 ? 0.6 : 1,
                        cursor: loading || formProgress < 60 ? 'not-allowed' : 'pointer',
                        fontWeight: '700'
                      }}
                    >
                      <span style={{ fontSize: '12px' }}>💾</span>
                      {loading ? 'Updating...' : 'Update Doctor Entry'}
                    </button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Patient & Doctor Selection */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafbfc' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
              <div>
                <label style={labelStyle}>
                  Patient <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <Tooltip title="Click to search and select a patient" arrow placement="top">
                  <input
                    type="text"
                    readOnly
                    placeholder="Click to select patient"
                    className={`input-field ${formData.PatientId ? 'success' : ''}`}
                    style={{ ...inputStyle, cursor: 'pointer', backgroundColor: 'white' }}
                    value={formData.patientName || ''}
                    onClick={() => setIsPatientModalOpen(true)}
                  />
                </Tooltip>
              </div>
              <div>
                <label style={labelStyle}>MRD No</label>
                <Tooltip title="Medical Record Number" arrow placement="top">
                  <input
                    type="text"
                    readOnly
                    className="input-field"
                    style={inputStyle}
                    value={formData.patientMRD || ''}
                  />
                </Tooltip>
              </div>
              <div>
                <label style={labelStyle}>Phone No</label>
                <input
                  type="text"
                  readOnly
                  className="input-field"
                  style={inputStyle}
                  value={formData.patientPhone || ''}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Doctor <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <Tooltip title="Select the attending doctor" arrow placement="top">
                  <select
                    className={`input-field ${formData.DoctorId ? 'success' : ''}`}
                    style={{ ...inputStyle, backgroundColor: 'white' }}
                    value={formData.DoctorId}
                    onChange={(e) => handleFormChange('DoctorId', e.target.value)}
                  >
                    <option value="">Select Doctor</option>
                    {doctors.map(doctor => (
                      <option key={doctor.Id} value={doctor.Id}>{doctor.Name}</option>
                    ))}
                  </select>
                </Tooltip>
              </div>
              <div>
                <label style={labelStyle}>Entry Date</label>
                <Tooltip title="Date of examination" arrow placement="top">
                  <input
                    type="date"
                    className="input-field"
                    style={inputStyle}
                    value={formData.DoctorEntryDate}
                    onChange={(e) => handleFormChange('DoctorEntryDate', e.target.value)}
                  />
                </Tooltip>
              </div>
            </div>

            <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={labelStyle}>
                  Chief Complaint <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <Tooltip title="Describe the patient's primary reason for visit (min 10 characters)" arrow placement="top">
                  <textarea
                    className={`input-field ${validationErrors.ChiefComplaint ? 'error' : formData.ChiefComplaint && formData.ChiefComplaint.length >= 10 ? 'success' : ''}`}
                    style={{ ...inputStyle, minHeight: '45px', resize: 'vertical', height: 'auto', padding: '4px 8px' }}
                    value={formData.ChiefComplaint}
                    onChange={(e) => handleFormChange('ChiefComplaint', e.target.value)}
                    placeholder="Enter patient's main complaint..."
                  />
                </Tooltip>
                {validationErrors.ChiefComplaint && (
                  <div style={{ fontSize: '9px', color: '#ef4444', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <span>⚠️</span> {validationErrors.ChiefComplaint}
                  </div>
                )}
                {formData.ChiefComplaint && formData.ChiefComplaint.length >= 10 && !validationErrors.ChiefComplaint && (
                  <div style={{ fontSize: '9px', color: '#10b981', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <span>✓</span> Looks good!
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Diagnosis</label>
                <Tooltip title="Enter the medical Diagnosis (min 5 characters)" arrow placement="top">
                  <textarea
                    className={`input-field ${validationErrors.Diagnosis ? 'error' : formData.Diagnosis && formData.Diagnosis.length >= 5 ? 'success' : ''}`}
                    style={{ ...inputStyle, minHeight: '45px', resize: 'vertical', height: 'auto', padding: '4px 8px' }}
                    value={formData.Diagnosis}
                    onChange={(e) => handleFormChange('Diagnosis', e.target.value)}
                    placeholder="Enter Diagnosis..."
                  />
                </Tooltip>
                {validationErrors.Diagnosis && (
                  <div style={{ fontSize: '9px', color: '#ef4444', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <span>⚠️</span> {validationErrors.Diagnosis}
                  </div>
                )}
                {formData.Diagnosis && formData.Diagnosis.length >= 5 && !validationErrors.Diagnosis && (
                  <div style={{ fontSize: '9px', color: '#10b981', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <span>✓</span> Looks good!
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <Box sx={{ background: '#f3f4f6', padding: '12px 16px' }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{
                minHeight: 'auto',
                '& .MuiTabs-flexContainer': {
                  gap: '8px'
                },
                '& .MuiTab-root': {
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'none',
                  minHeight: '40px',
                  padding: '8px 12px',
                  color: '#6b7280',
                  background: '#e5e7eb',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  '&:hover': {
                    background: '#f9fafb',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#ffffff',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontWeight: '700',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Tab
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                    <span style={{ fontSize: '20px', lineHeight: '1' }}>🩺</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Observations</span>
                    {sectionCompletion.observations && (
                      <span style={{
                        fontSize: '10px',
                        color: '#10b981',
                        background: '#d1fae5',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700'
                      }}>✓</span>
                    )}
                  </div>
                }
                iconPosition="start"
              />
              <Tab
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                    <span style={{ fontSize: '20px', lineHeight: '1' }}>💊</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Prescriptions</span>
                    {sectionCompletion.prescriptions && (
                      <span style={{
                        fontSize: '10px',
                        color: '#10b981',
                        background: '#d1fae5',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700'
                      }}>✓</span>
                    )}
                  </div>
                }
                iconPosition="start"
              />
              <Tab
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                    <span style={{ fontSize: '20px', lineHeight: '1' }}>🏥</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Treatments</span>
                    {sectionCompletion.treatments && (
                      <span style={{
                        fontSize: '10px',
                        color: '#10b981',
                        background: '#d1fae5',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700'
                      }}>✓</span>
                    )}
                  </div>
                }
                iconPosition="start"
              />
              <Tab
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                    <span style={{ fontSize: '20px', lineHeight: '1' }}>📝</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Questionnaire</span>
                    {sectionCompletion.Questionnaire && (
                      <span style={{
                        fontSize: '10px',
                        color: '#10b981',
                        background: '#d1fae5',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700'
                      }}>✓</span>
                    )}
                  </div>
                }
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <div className="tab-content" style={{ padding: '12px 16px' }}>
            {/* Observations Tab */}
            {activeTab === 0 && (
              <div>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                  padding: '10px',
                  borderRadius: '6px',
                  marginBottom: '12px'
                }}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 8px 0' }}>
                    Add Observation
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: '8px', alignItems: 'end' }}>
                    <div>
                      <label style={labelStyle}>Observation Type *</label>
                      <select
                        className="input-field"
                        style={inputStyle}
                        value={currentObservation.ObservationType}
                        onChange={(e) => setCurrentObservation({ ...currentObservation, ObservationType: e.target.value })}
                      >
                        <option value="">Select Type</option>
                        <option value="Blood Pressure">Blood Pressure</option>
                        <option value="Temperature">Temperature</option>
                        <option value="Heart Rate">Heart Rate</option>
                        <option value="Weight">Weight</option>
                        <option value="Height">Height</option>
                        <option value="Blood Sugar">Blood Sugar</option>
                        <option value="Oxygen Level">Oxygen Level</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Value *</label>
                      <input
                        type="text"
                        className="input-field"
                        style={inputStyle}
                        value={currentObservation.ObservationValue}
                        onChange={(e) => setCurrentObservation({ ...currentObservation, ObservationValue: e.target.value })}
                        placeholder="Enter value"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Unit</label>
                      <input
                        type="text"
                        className="input-field"
                        style={inputStyle}
                        value={currentObservation.Unit}
                        onChange={(e) => setCurrentObservation({ ...currentObservation, Unit: e.target.value })}
                        placeholder="e.g., mmHg, °F"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Remarks</label>
                      <input
                        type="text"
                        className="input-field"
                        style={inputStyle}
                        value={currentObservation.Remarks}
                        onChange={(e) => setCurrentObservation({ ...currentObservation, Remarks: e.target.value })}
                        placeholder="Additional notes"
                      />
                    </div>
                    <Tooltip title="Add this observation to the list" arrow placement="right">
                      <button
                        onClick={addObservation}
                        style={{
                          ...buttonStyle,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          height: '32px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <span style={{ fontSize: '14px' }}>➕</span>
                        Add Observation
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {/* Observations Table */}
                {observations.length > 0 && (
                  <TableContainer component={Paper} className="advanced-table">
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{
                          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                          borderBottom: '3px solid #8b9aed'
                        }}>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Type</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Value</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Unit</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Remarks</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {observations.map((obs, index) => (
                          <TableRow
                            key={obs.id}
                            className="table-row-animated"
                            style={{
                              background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                              borderLeft: '4px solid transparent',
                              animationDelay: `${index * 0.05}s`
                            }}
                          >
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{obs.ObservationType}</TableCell>
                            <TableCell style={{ fontSize: '13px', fontWeight: '700', padding: '6px 10px', color: '#0f172a' }}>{obs.ObservationValue}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b', fontStyle: 'italic' }}>{obs.Unit}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b' }}>{obs.Remarks}</TableCell>
                            <TableCell style={{ textAlign: 'center', padding: '6px 10px' }}>
                              <button
                                onClick={() => deleteObservation(index)}
                                style={{
                                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                  border: '1px solid #fca5a5',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '3px 8px',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'scale(1.1) rotate(5deg)';
                                  e.target.style.background = 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1) rotate(0deg)';
                                  e.target.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                                  e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.1)';
                                }}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </div>
            )}

            {/* Prescriptions Tab */}
            {activeTab === 1 && (
              <div>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                  padding: '10px',
                  borderRadius: '6px',
                  marginBottom: '12px'
                }}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 8px 0' }}>
                    Add Prescription
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 1fr 0.7fr 1.5fr auto', gap: '8px', alignItems: 'end' }}>
                    <div>
                      <label style={labelStyle}>Medicine Name *</label>
                      <select
                        className="input-field"
                        style={inputStyle}
                        value={currentPrescription.ItemId}
                        onChange={(e) => {
                          const selectedMedicine = medicines.find(m => m.Id === parseInt(e.target.value));
                          setCurrentPrescription({
                            ...currentPrescription,
                            ItemId: e.target.value,
                            MedicineName: selectedMedicine ? selectedMedicine.Name : ''
                          });
                        }}
                      >
                        <option value="">Select Medicine</option>
                        {medicines.map(medicine => (
                          <option key={medicine.Id} value={medicine.Id}>{medicine.Name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Dosage *</label>
                      <input
                        type="text"
                        className="input-field"
                        style={inputStyle}
                        value={currentPrescription.Dosage}
                        onChange={(e) => setCurrentPrescription({ ...currentPrescription, Dosage: e.target.value })}
                        placeholder="e.g., 500mg"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Frequency</label>
                      <select
                        className="input-field"
                        style={inputStyle}
                        value={currentPrescription.Frequency}
                        onChange={(e) => setCurrentPrescription({ ...currentPrescription, Frequency: e.target.value })}
                      >
                        <option value="">Select Frequency</option>
                        <option value="Once Daily">Once Daily</option>
                        <option value="Twice Daily">Twice Daily</option>
                        <option value="Three Times Daily">Three Times Daily</option>
                        <option value="Four Times Daily">Four Times Daily</option>
                        <option value="Every 4 Hours">Every 4 Hours</option>
                        <option value="Every 6 Hours">Every 6 Hours</option>
                        <option value="As Needed">As Needed</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Duration</label>
                      <input
                        type="text"
                        className="input-field"
                        style={inputStyle}
                        value={currentPrescription.Duration}
                        onChange={(e) => setCurrentPrescription({ ...currentPrescription, Duration: e.target.value })}
                        placeholder="e.g., 7 days"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Instructions</label>
                      <input
                        type="text"
                        className="input-field"
                        style={inputStyle}
                        value={currentPrescription.Instructions}
                        onChange={(e) => setCurrentPrescription({ ...currentPrescription, Instructions: e.target.value })}
                        placeholder="e.g., Take after meals"
                      />
                    </div>
                    <Tooltip title="Add this prescription to the list" arrow placement="right">
                      <button
                        onClick={addPrescription}
                        style={{
                          ...buttonStyle,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          height: '32px',
                          whiteSpace: 'nowrap',
                          width: 'auto',
                          minWidth: '140px'
                        }}
                      >
                        <span style={{ fontSize: '14px' }}>➕</span>
                        Add Prescription
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {/* Prescriptions Table */}
                {prescriptions.length > 0 && (
                  <TableContainer component={Paper} className="advanced-table">
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderBottom: '3px solid #8b9aed' }}><TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Medicine</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Dosage</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Frequency</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Duration</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Instructions</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {prescriptions.map((pres, index) => (
                          <TableRow
                            key={pres.id}
                            className="table-row-animated"
                            style={{
                              background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                              borderLeft: '4px solid transparent',
                              animationDelay: `${index * 0.05}s`
                            }}
                          >
                            <TableCell style={{ fontSize: '13px', fontWeight: '700', padding: '6px 10px', color: '#0f172a' }}>{pres.MedicineName}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{pres.Dosage}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b' }}>{pres.Frequency}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b' }}>{pres.Duration}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b', fontStyle: 'italic' }}>{pres.Instructions}</TableCell>
                            <TableCell style={{ textAlign: 'center', padding: '6px 8px' }}>
                              <button
                                onClick={() => deletePrescription(index)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '0px 2px',
                                  transition: 'transform 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </div>
            )}

            {/* Treatments Tab */}
            {activeTab === 2 && (
              <div>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                  padding: '10px',
                  borderRadius: '6px',
                  marginBottom: '12px'
                }}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 8px 0' }}>
                    Add Treatment
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.8fr 0.8fr 0.6fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                    <div>
                      <label style={labelStyle}>Treatment Name *</label>
                      <select
                        className="input-field"
                        style={inputStyle}
                        value={currentTreatment.TreatmentId}
                        onChange={(e) => {
                          const selectedTreatment = treatmentsList.find(t => t.Id === parseInt(e.target.value));
                          setCurrentTreatment({
                            ...currentTreatment,
                            TreatmentId: e.target.value,
                            TreatmentName: selectedTreatment ? selectedTreatment.Name : '',
                            SessionId: '',
                            TreatmentSessionName: ''
                          });
                          if (e.target.value) {
                            fetchTreatmentSessions(e.target.value);
                          } else {
                            setTreatmentSessions([]);
                          }
                        }}
                      >
                        <option value="">Select Treatment</option>
                        {treatmentsList.map(treatment => (
                          <option key={treatment.Id} value={treatment.Id}>{treatment.Name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Session</label>
                      <select
                        className="input-field"
                        style={inputStyle}
                        value={currentTreatment.SessionId}
                        onChange={(e) => {
                          const selectedSession = treatmentSessions.find(s => s.Id === parseInt(e.target.value));
                          setCurrentTreatment({
                            ...currentTreatment,
                            SessionId: e.target.value,
                            TreatmentSessionName: selectedSession ? selectedSession.Name : '',
                            Rate: selectedSession && selectedSession.Rate ? selectedSession.Rate : ''
                          });
                        }}
                        disabled={!currentTreatment.TreatmentId}
                      >
                        <option value="">Select Session</option>
                        {treatmentSessions.map(session => (
                          <option key={session.Id} value={session.Id}>{session.Name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Start Date</label>
                      <input
                        type="date"
                        className="input-field"
                        style={inputStyle}
                        value={currentTreatment.StartDate}
                        onChange={(e) => setCurrentTreatment({ ...currentTreatment, StartDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>End Date</label>
                      <input
                        type="date"
                        className="input-field"
                        style={inputStyle}
                        value={currentTreatment.EndDate}
                        onChange={(e) => setCurrentTreatment({ ...currentTreatment, EndDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Rate</label>
                      <input
                        type="number"
                        className="input-field"
                        style={{ ...inputStyle, textAlign: 'right' }}
                        value={currentTreatment.Rate}
                        onChange={(e) => setCurrentTreatment({ ...currentTreatment, Rate: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Description</label>
                      <input
                        type="text"
                        className="input-field"
                        style={inputStyle}
                        value={currentTreatment.Description}
                        onChange={(e) => setCurrentTreatment({ ...currentTreatment, Description: e.target.value })}
                        placeholder="Treatment Description"
                      />
                    </div>
                    <Tooltip title="Add this treatment to the list" arrow placement="right">
                      <button
                        onClick={addTreatment}
                        style={{
                          ...buttonStyle,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          height: '32px',
                          whiteSpace: 'nowrap',
                          width: 'auto',
                          minWidth: '140px'
                        }}
                      >
                        <span style={{ fontSize: '14px' }}>➕</span>
                        Add Treatment
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {/* Treatments Table */}
                {treatments.length > 0 && (
                  <TableContainer component={Paper} className="advanced-table">
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{
                          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                          borderBottom: '3px solid #8b9aed'
                        }}>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Treatment</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Session</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Description</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Start Date</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>End Date</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'right', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Rate</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {treatments.map((treat, index) => (
                          <TableRow
                            key={treat.id}
                            className="table-row-animated"
                            style={{
                              background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                              borderLeft: '4px solid transparent',
                              animationDelay: `${index * 0.05}s`
                            }}
                          >
                            <TableCell style={{ fontSize: '13px', fontWeight: '700', padding: '6px 10px', color: '#0f172a' }}>{treat.TreatmentName}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#8b5cf6', fontWeight: '600' }}>{treat.TreatmentSessionName || '-'}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#64748b' }}>{treat.Description}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{treat.StartDate}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{treat.EndDate}</TableCell>
                            <TableCell style={{ fontSize: '13px', textAlign: 'right', padding: '6px 10px', color: '#059669', fontWeight: '700' }}>{treat.Rate}</TableCell>
                            <TableCell style={{ textAlign: 'center', padding: '6px 10px' }}>
                              <button
                                onClick={() => deleteTreatment(index)}
                                style={{
                                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                  border: '1px solid #fca5a5',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '3px 8px',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'scale(1.1) rotate(5deg)';
                                  e.target.style.background = 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1) rotate(0deg)';
                                  e.target.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                                  e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.1)';
                                }}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </div>
            )}

            {/* Questionnaire Tab */}
            {activeTab === 3 && (
              <div>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                  padding: '10px',
                  borderRadius: '6px',
                  marginBottom: '12px'
                }}>
                  <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#667eea', margin: '0 0 8px 0' }}>
                    Add Question & Answer
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 150px 1.5fr auto', gap: '8px', alignItems: 'end', marginBottom: '8px' }}>
                    <div>
                      <label style={labelStyle}>Select Question from List *</label>
                      <select
                        className="input-field"
                        style={inputStyle}
                        value={currentQuestion.QuestioniertId}
                        onChange={(e) => {
                          const selectedQuestion = QuestionsList.find(q => q.Id === parseInt(e.target.value));
                          setCurrentQuestion({
                            ...currentQuestion,
                            QuestioniertId: e.target.value,
                            Question: selectedQuestion ? selectedQuestion.Question : ''
                          });
                        }}
                      >
                        <option value="">Select Question</option>
                        {QuestionsList.map(q => (
                          <option key={q.Id} value={q.Id}>{q.Question}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Answer Type *</label>
                      <select
                        className="input-field"
                        style={inputStyle}
                        value={currentQuestion.AnswerType}
                        onChange={(e) => setCurrentQuestion({ ...currentQuestion, AnswerType: e.target.value, Answer: '' })}
                      >
                        <option value="text">Text Input</option>
                        <option value="select">Select Option</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Answer *</label>
                      {currentQuestion.AnswerType === 'select' ? (
                        <select
                          className="input-field"
                          style={inputStyle}
                          value={currentQuestion.Answer}
                          onChange={(e) => setCurrentQuestion({ ...currentQuestion, Answer: e.target.value })}
                        >
                          <option value="">Select Answer</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                          <option value="Sometimes">Sometimes</option>
                          <option value="Frequently">Frequently</option>
                          <option value="Rarely">Rarely</option>
                          <option value="Never">Never</option>
                          <option value="Mild">Mild</option>
                          <option value="Moderate">Moderate</option>
                          <option value="Severe">Severe</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="input-field"
                          style={inputStyle}
                          value={currentQuestion.Answer}
                          onChange={(e) => setCurrentQuestion({ ...currentQuestion, Answer: e.target.value })}
                          placeholder="Enter Answer"
                        />
                      )}
                    </div>
                    <Tooltip title="Add this Question to the Questionnaire" arrow placement="right">
                      <button
                        onClick={addQuestion}
                        style={{
                          ...buttonStyle,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          height: '32px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <span style={{ fontSize: '14px' }}>➕</span>
                        Add Question
                      </button>
                    </Tooltip>
                  </div>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>💡</span>
                    <span>Select a Question from the dropdown, choose Answer type (text or select), then provide the Answer</span>
                  </div>
                </div>

                {/* Questionnaire Table */}
                {Questionnaire.length > 0 && (
                  <TableContainer component={Paper} className="advanced-table">
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{
                          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                          borderBottom: '3px solid #8b9aed'
                        }}>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Question ID</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Question</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Answer</TableCell>
                          <TableCell style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '11px', textAlign: 'center', padding: '6px 10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Questionnaire.map((q, index) => (
                          <TableRow
                            key={q.id}
                            className="table-row-animated"
                            style={{
                              background: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                              borderLeft: '4px solid transparent',
                              animationDelay: `${index * 0.05}s`
                            }}
                          >
                            <TableCell style={{ fontSize: '12px', fontWeight: '700', padding: '6px 10px', color: '#667eea' }}>
                              {q.QuestioniertId ? `#${q.QuestioniertId}` : 'N/A'}
                            </TableCell>
                            <TableCell style={{ fontSize: '13px', fontWeight: '700', padding: '6px 10px', color: '#0f172a' }}>{q.Question}</TableCell>
                            <TableCell style={{ fontSize: '12px', padding: '6px 10px', color: '#475569', fontWeight: '600' }}>{q.Answer}</TableCell>
                            <TableCell style={{ textAlign: 'center', padding: '6px 10px' }}>
                              <button
                                onClick={() => deleteQuestion(index)}
                                style={{
                                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                  border: '1px solid #fca5a5',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '3px 8px',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'scale(1.1) rotate(5deg)';
                                  e.target.style.background = 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1) rotate(0deg)';
                                  e.target.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                                  e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.1)';
                                }}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </div>
            )}
          </div>

        </div>

        {/* Patient Selection Modal */}
        {isPatientModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
            }}>
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'white', margin: 0 }}>
                    Select Patient
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px', margin: '4px 0 0 0' }}>
                    Search and select a patient for this entry
                  </p>
                </div>
                <button
                  onClick={() => setIsPatientModalOpen(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                >
                  ×
                </button>
              </div>

              {/* Search Bar */}
              <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
                <input
                  type="text"
                  placeholder="🔍 Search by MRD Number, Name, or Phone..."
                  value={patientSearchQuery}
                  onChange={(e) => setPatientSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '13px',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Patient Table */}
              <div style={{ maxHeight: 'calc(80vh - 200px)', overflowY: 'auto' }}>
                {loadingPatients ? (
                  <div style={{ padding: '20px' }}>
                    {[1, 2, 3, 4, 5].map((item) => (
                      <div key={item} style={{ marginBottom: '12px', display: 'flex', gap: '12px' }}>
                        <div className="shimmer-loading" style={{ height: '40px', borderRadius: '6px', flex: '1' }}></div>
                        <div className="shimmer-loading" style={{ height: '40px', borderRadius: '6px', flex: '2' }}></div>
                        <div className="shimmer-loading" style={{ height: '40px', borderRadius: '6px', flex: '1' }}></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr>
                        <th style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          padding: '12px',
                          fontSize: '11px',
                          fontWeight: '700',
                          textAlign: 'left',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          MRD No
                        </th>
                        <th style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          padding: '12px',
                          fontSize: '11px',
                          fontWeight: '700',
                          textAlign: 'left',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Patient Name
                        </th>
                        <th style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          padding: '12px',
                          fontSize: '11px',
                          fontWeight: '700',
                          textAlign: 'left',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Phone No
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients
                        .filter(patient =>
                          patient.MRDNo?.toLowerCase().includes(patientSearchQuery.toLowerCase()) ||
                          patient.Name?.toLowerCase().includes(patientSearchQuery.toLowerCase()) ||
                          patient.ContactNumber1?.toLowerCase().includes(patientSearchQuery.toLowerCase())
                        )
                        .map((patient, index) => (
                          <tr
                            key={patient.Id}
                            onClick={() => {
                              handleFormChange('PatientId', patient.Id);
                              handleFormChange('patientName', patient.Name);
                              handleFormChange('patientMRD', patient.MRDNo);
                              handleFormChange('patientPhone', patient.ContactNumber1);
                              setIsPatientModalOpen(false);
                              setPatientSearchQuery('');
                            }}
                            style={{
                              cursor: 'pointer',
                              transition: 'background-color 0.15s ease',
                              backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f9ff'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa'}
                          >
                            <td style={{
                              padding: '12px',
                              fontSize: '12px',
                              color: '#667eea',
                              fontWeight: '600',
                              borderBottom: '1px solid #e5e7eb'
                            }}>
                              {patient.MRDNo || 'N/A'}
                            </td>
                            <td style={{
                              padding: '12px',
                              fontSize: '12px',
                              color: '#1f2937',
                              fontWeight: '500',
                              borderBottom: '1px solid #e5e7eb'
                            }}>
                              {patient.Name}
                            </td>
                            <td style={{
                              padding: '12px',
                              fontSize: '12px',
                              color: '#1f2937',
                              fontWeight: '500',
                              borderBottom: '1px solid #e5e7eb'
                            }}>
                              {patient.ContactNumber1 || 'N/A'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
                {!loadingPatients && patients.filter(patient =>
                  patient.MRDNo?.toLowerCase().includes(patientSearchQuery.toLowerCase()) ||
                  patient.Name?.toLowerCase().includes(patientSearchQuery.toLowerCase()) ||
                  patient.ContactNumber1?.toLowerCase().includes(patientSearchQuery.toLowerCase())
                ).length === 0 && (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '13px'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>No patients found</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>Try adjusting your search criteria</div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Fade>
  );
};

export default DoctorEntryEdit;
